import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Button, Alert, Stack, Chip, Grid, ListItemButton,
  ListItemText, Badge, Dialog, DialogTitle, DialogContent, DialogActions, Divider, Collapse,
  Snackbar,
} from '@mui/material';
import {
  CardGiftcard, Lightbulb, ManageAccounts, ExpandMore, ExpandLess, Assignment, Forum, Add,
} from '@mui/icons-material';
import Confetti from 'react-confetti';
import Layout from '../components/Layout';
import { GiftIdeaForm, GiftIdeaList, IdeaFormValues } from '../components/GiftIdeas';
import AnonThread from '../components/AnonThread';
import { apiService, errorMessage } from '../services/api';
import { Group, Member, MyMatch, GiftIdea, AnonMessage } from '../types';
import { formatBudget, formatLongDate } from '../utils/formatters';
import { getSurveyQuestionsByCategory } from '../utils/surveyQuestions';

const GroupHome: React.FC = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();

  const [group, setGroup] = useState<Group | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [summary, setSummary] = useState<Record<string, number>>({});
  const [myIdeas, setMyIdeas] = useState<GiftIdea[]>([]);
  const [messages, setMessages] = useState<{ asGiver: AnonMessage[]; asReceiver: AnonMessage[] }>({ asGiver: [], asReceiver: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Match reveal
  const [match, setMatch] = useState<MyMatch | null>(null);
  const [revealing, setRevealing] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [confetti, setConfetti] = useState(false);
  const [showSurvey, setShowSurvey] = useState(false);

  // Suggest ideas
  const [subject, setSubject] = useState<Member | null>(null);
  const [subjectIdeas, setSubjectIdeas] = useState<GiftIdea[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<GiftIdea | null>(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState<'giver' | 'receiver' | null>(null);

  const myId = group?.myParticipantId || null;
  const isParticipant = Boolean(myId);
  const isOrganizer = group?.myRole === 'organizer';

  const load = useCallback(async () => {
    if (!groupId) return;
    try {
      const [g, m, s, mine, msgs] = await Promise.all([
        apiService.getGroup(groupId),
        apiService.getMembers(groupId),
        apiService.getGiftIdeaSummary(groupId),
        apiService.getGiftIdeas(groupId, { mine: true }),
        apiService.getMessages(groupId),
      ]);
      setGroup(g);
      setMembers(m);
      setSummary(s);
      setMyIdeas(mine);
      setMessages(msgs);
    } catch (e: any) {
      if (e?.response?.status === 403 || e?.response?.status === 404) {
        setError("You're not part of this group, or it no longer exists.");
      } else {
        setError(errorMessage(e, 'Failed to load this group'));
      }
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => { load(); }, [load]);

  const reveal = async () => {
    if (!groupId) return;
    setRevealing(true);
    try {
      const data = await apiService.getMyMatch(groupId);
      setMatch(data);
      setRevealed(true);
      setConfetti(true);
      setTimeout(() => setConfetti(false), 6000);
    } catch (e) {
      setError(errorMessage(e, 'Could not load your match'));
    } finally {
      setRevealing(false);
    }
  };

  const openSubject = async (m: Member) => {
    if (!groupId) return;
    setSubject(m);
    setSubjectIdeas([]);
    try {
      setSubjectIdeas(await apiService.getGiftIdeas(groupId, { subject: m.id }));
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  const refreshIdeas = async () => {
    if (!groupId) return;
    const [s, mine] = await Promise.all([
      apiService.getGiftIdeaSummary(groupId),
      apiService.getGiftIdeas(groupId, { mine: true }),
    ]);
    setSummary(s);
    setMyIdeas(mine);
    if (subject) setSubjectIdeas(await apiService.getGiftIdeas(groupId, { subject: subject.id }));
    if (match) setMatch(await apiService.getMyMatch(groupId));
  };

  const saveIdea = async (values: IdeaFormValues) => {
    if (!groupId) return;
    setSaving(true);
    try {
      if (editing) {
        await apiService.updateGiftIdea(groupId, editing.id, {
          title: values.title, url: values.url, notes: values.notes,
          priceEstimate: values.priceEstimate === '' ? null : (values.priceEstimate as any),
        });
        setToast('Idea updated');
      } else if (subject) {
        await apiService.createGiftIdea(groupId, {
          subjectParticipantId: subject.id, title: values.title, url: values.url,
          notes: values.notes, priceEstimate: values.priceEstimate || null,
        });
        setToast(`Idea added for ${subject.name}`);
      }
      setFormOpen(false);
      setEditing(null);
      await refreshIdeas();
    } catch (e) {
      setError(errorMessage(e, 'Could not save the idea'));
    } finally {
      setSaving(false);
    }
  };

  const deleteIdea = async (idea: GiftIdea) => {
    if (!groupId || !window.confirm(`Delete "${idea.title}"?`)) return;
    try {
      await apiService.deleteGiftIdea(groupId, idea.id);
      await refreshIdeas();
    } catch (e) {
      setError(errorMessage(e, 'Could not delete the idea'));
    }
  };

  const send = async (as: 'giver' | 'receiver', body: string) => {
    if (!groupId) return;
    setSending(as);
    try {
      await apiService.sendMessage(groupId, body, as);
      setMessages(await apiService.getMessages(groupId));
    } catch (e) {
      setError(errorMessage(e, 'Could not send the message'));
    } finally {
      setSending(null);
    }
  };

  const others = members.filter((m) => !m.isMe);
  const matched = group?.status === 'matched' || group?.status === 'completed';

  return (
    <Layout title={group?.name || 'Secret Santa'} backTo="/dashboard" maxWidth="md" loading={loading}>
      {confetti && <Confetti recycle={false} numberOfPieces={350} />}

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      {group && (
        <Stack spacing={3}>
          {/* Group header */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontFamily: '"Mountains of Christmas", cursive', fontWeight: 700, color: 'primary.main' }}>
                    {group.name} {group.year}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                    {group.budget ? <Chip size="small" label={`Budget ${formatBudget(group.budget)}`} /> : null}
                    {group.exchangeDate ? <Chip size="small" label={`Exchange ${formatLongDate(group.exchangeDate)}`} /> : null}
                    {group.exchangeLocation ? <Chip size="small" label={group.exchangeLocation} /> : null}
                    {members.length > 0 && <Chip size="small" variant="outlined" label={`${members.length} people`} />}
                  </Stack>
                </Box>
                {isOrganizer && (
                  <Button variant="outlined" startIcon={<ManageAccounts />} onClick={() => navigate(`/group/${group.id}`)}>
                    Manage group
                  </Button>
                )}
              </Box>
              {!isParticipant && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  You organize this group but aren't a participant. Add yourself from the Manage page to draw a name.
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Match card */}
          {isParticipant && (
            <Card sx={{ bgcolor: '#fbf6ea', borderColor: '#e4d3a4' }}>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                {!matched ? (
                  <>
                    <Typography variant="h5" sx={{ fontFamily: 'inherit', color: '#5c4520', mb: 1 }}>The draw hasn't happened yet</Typography>
                    <Typography sx={{ color: '#5c4520' }}>
                      You'll get an email{group.settings?.reminders?.matchReady !== false ? ' (and a text if you opted in)' : ''} when your person is ready.
                      In the meantime, add gift ideas for people you know well below.
                    </Typography>
                  </>
                ) : !revealed ? (
                  <>
                    <Typography variant="h5" sx={{ fontFamily: 'inherit', color: '#5c4520', mb: 2 }}>Your person is ready</Typography>
                    <Button
                      size="large" variant="contained" startIcon={<CardGiftcard />} onClick={reveal} disabled={revealing}
                      sx={{ py: 1.25, px: 3 }}
                    >
                      {revealing ? 'Opening...' : 'Tap to reveal'}
                    </Button>
                    <Typography variant="body2" display="block" sx={{ color: '#5c4520', mt: 2 }}>
                      Make sure nobody's looking over your shoulder.
                    </Typography>
                  </>
                ) : match && (
                  <>
                    <Typography sx={{ color: '#5c4520' }}>You're the Secret Santa for</Typography>
                    <Typography sx={{ fontFamily: '"Mountains of Christmas", cursive', fontSize: { xs: '2.6rem', md: '3.4rem' }, color: 'primary.main', my: 1, lineHeight: 1 }}>
                      {match.recipient.name}
                    </Typography>
                    <Button size="small" onClick={() => setRevealed(false)} sx={{ color: '#5c4520', mt: 1 }}>Hide</Button>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Ideas for my recipient + Q&A as giver */}
          {revealed && match && (
            <>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Lightbulb color="primary" /> Gift ideas for {match.recipient.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Suggested by people in this group who know them well. {match.recipient.name} can't see these.
                  </Typography>
                  <GiftIdeaList
                    ideas={match.giftIdeas}
                    myParticipantId={myId}
                    emptyText={`Nobody has suggested anything for ${match.recipient.name} yet. Check back later, or ask them anonymously below.`}
                  />
                  {match.surveyResponse && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Button
                        size="small" startIcon={<Assignment />} endIcon={showSurvey ? <ExpandLess /> : <ExpandMore />}
                        onClick={() => setShowSurvey(!showSurvey)}
                      >
                        {match.recipient.name}'s gift preferences
                      </Button>
                      <Collapse in={showSurvey}>
                        <Box sx={{ mt: 1 }}>
                          {Object.entries(getSurveyQuestionsByCategory()).map(([category, questions]) => {
                            const answered = questions.filter((q) => {
                              const v = match.surveyResponse!.responses[q.id];
                              return v && (!Array.isArray(v) || v.length > 0);
                            });
                            if (answered.length === 0) return null;
                            return (
                              <Box key={category} sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" color="primary">{category}</Typography>
                                {answered.map((q) => (
                                  <Box key={q.id} sx={{ ml: 1, mb: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary">{q.text}</Typography>
                                    <Typography variant="body2">
                                      {Array.isArray(match.surveyResponse!.responses[q.id])
                                        ? match.surveyResponse!.responses[q.id].join(', ')
                                        : String(match.surveyResponse!.responses[q.id])}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            );
                          })}
                        </Box>
                      </Collapse>
                    </>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Forum color="primary" /> Ask {match.recipient.name} anonymously
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    They'll see the question from "your Secret Santa" and can reply here. Your name is never shown.
                  </Typography>
                  <AnonThread
                    messages={messages.asGiver}
                    otherLabel={match.recipient.name}
                    placeholder="e.g. What's your shoe size?"
                    emptyText="No messages yet."
                    sending={sending === 'giver'}
                    onSend={(body) => send('giver', body)}
                  />
                </CardContent>
              </Card>
            </>
          )}

          {/* Messages from my Secret Santa */}
          {isParticipant && matched && (
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Forum color="secondary" /> Messages from your Secret Santa
                  {messages.asReceiver.some((m) => !m.fromMe) && (
                    <Chip size="small" color="secondary" label={messages.asReceiver.filter((m) => !m.fromMe).length} />
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Whoever drew you can ask questions here without revealing who they are. You can reply.
                </Typography>
                <AnonThread
                  messages={messages.asReceiver}
                  otherLabel="Your Secret Santa"
                  placeholder="Reply to your Secret Santa..."
                  emptyText="Your Secret Santa hasn't asked anything yet."
                  sending={sending === 'receiver'}
                  onSend={(body) => send('receiver', body)}
                />
              </CardContent>
            </Card>
          )}

          {/* Suggest gift ideas for others */}
          {isParticipant && (
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Lightbulb color="secondary" /> Suggest gift ideas
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Know what your partner or kids actually want? Add ideas for them. Everyone in this group can see
                  the ideas except the person they're for, so whoever draws them gets a head start.
                </Typography>
                <Grid container spacing={1}>
                  {others.map((m) => (
                    <Grid item xs={12} sm={6} key={m.id}>
                      <ListItemButton onClick={() => openSubject(m)} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                        <ListItemText primary={m.name} secondary={summary[m.id] ? `${summary[m.id]} idea${summary[m.id] === 1 ? '' : 's'}` : 'No ideas yet'} />
                        <Badge badgeContent={summary[m.id] || 0} color="primary" showZero={false}>
                          <Add color="action" />
                        </Badge>
                      </ListItemButton>
                    </Grid>
                  ))}
                </Grid>
                {myIdeas.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Ideas you've suggested</Typography>
                    <GiftIdeaList
                      ideas={myIdeas} myParticipantId={myId} showSubject
                      onEdit={(idea) => { setEditing(idea); setFormOpen(true); }}
                      onDelete={deleteIdea}
                    />
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Survey */}
          {isParticipant && group.settings?.surveyEnabled !== false && (
            <Card>
              <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="h6">Your gift preferences</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sizes, favorites, and dislikes. Only the person who draws you (and the organizer) can see it.
                  </Typography>
                </Box>
                <Button variant="outlined" startIcon={<Assignment />} onClick={() => navigate(`/survey/${group.id}/${myId}`)}>
                  Fill out survey
                </Button>
              </CardContent>
            </Card>
          )}
        </Stack>
      )}

      {/* Ideas for a chosen person */}
      <Dialog open={Boolean(subject) && !formOpen} onClose={() => setSubject(null)} maxWidth="sm" fullWidth>
        {subject && (
          <>
            <DialogTitle>Gift ideas for {subject.name}</DialogTitle>
            <DialogContent>
              <GiftIdeaList
                ideas={subjectIdeas} myParticipantId={myId}
                emptyText={`No ideas for ${subject.name} yet. Be the first!`}
                onEdit={(idea) => { setEditing(idea); setFormOpen(true); }}
                onDelete={deleteIdea}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSubject(null)}>Close</Button>
              <Button variant="contained" startIcon={<Add />} onClick={() => { setEditing(null); setFormOpen(true); }}>
                Add idea
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <GiftIdeaForm
        open={formOpen}
        forName={subject?.name || editing?.subjectName || ''}
        initial={editing}
        saving={saving}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        onSave={saveIdea}
      />

      <Snackbar open={Boolean(toast)} autoHideDuration={3000} onClose={() => setToast(null)} message={toast} />
    </Layout>
  );
};

export default GroupHome;
